import type { SupabaseClient } from '../supabase/client';
import type {
  AdminOrder,
  DeclineManualOrderResult,
  ManualApprovalErrorCode,
  ManualApprovalTransitionResult,
  OrderErrorKey,
} from '../types/order';

interface RawManualApprovalRow {
  readonly r_success: boolean;
  readonly r_error_code: string | null;
  readonly r_order: Record<string, unknown> | null;
}

interface RawDeclineManualApprovalRow extends RawManualApprovalRow {
  readonly r_inventory_restored: Array<{
    readonly product_id: string;
    readonly variant_id: string;
    readonly quantity: number;
  }> | null;
  readonly r_notification_intent_id: string | null;
  readonly r_notification_status: 'pending' | 'skipped' | 'already_exists' | null;
}

function unwrapManualApprovalRow<T>(data: unknown, fnName: string): T {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error(`[${fnName}] empty result from RPC`);
  return row as T;
}

export async function approveManualOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<ManualApprovalTransitionResult> {
  const { data, error } = await client.rpc('approve_manual_order', {
    target_order_id: orderId,
  } as never);
  if (error) throw error;

  const row = unwrapManualApprovalRow<RawManualApprovalRow>(data, 'approveManualOrder');
  return {
    success: row.r_success,
    error_code: (row.r_error_code as ManualApprovalErrorCode) ?? null,
    order: (row.r_order as AdminOrder | null) ?? null,
  };
}

export async function declineManualOrder(
  client: SupabaseClient,
  orderId: string,
): Promise<DeclineManualOrderResult> {
  const { data, error } = await client.rpc('decline_manual_order', {
    target_order_id: orderId,
  } as never);
  if (error) throw error;

  const row = unwrapManualApprovalRow<RawDeclineManualApprovalRow>(data, 'declineManualOrder');
  return {
    success: row.r_success,
    error_code: (row.r_error_code as ManualApprovalErrorCode) ?? null,
    order: (row.r_order as AdminOrder | null) ?? null,
    inventory_restored: row.r_inventory_restored ?? [],
    notification_intent_id: row.r_notification_intent_id,
    notification_status: row.r_notification_status,
  };
}

export function mapManualApprovalError(
  err: unknown,
  rpcResult?: { readonly success: boolean; readonly error_code: string | null },
  action: 'approve' | 'decline' = 'approve',
): OrderErrorKey {
  if (rpcResult && !rpcResult.success) {
    switch (rpcResult.error_code) {
      case 'INVALID_TRANSITION':
      case 'APPROVAL_NOT_REQUIRED':
        return 'order.errors.invalidTransition';
      case 'UNAUTHORIZED':
        return 'order.errors.transitionUnauthorized';
      case 'NOT_FOUND':
        return 'order.errors.orderNotFound';
      case 'DECLINE_FAILED':
      case 'NOTIFICATION_ENQUEUE_FAILED':
        return action === 'decline' ? 'order.errors.rejectFailed' : 'order.errors.unknown';
      default:
        return 'order.errors.unknown';
    }
  }

  if (err && typeof err === 'object') {
    const maybe = err as {
      readonly code?: string;
      readonly message?: string;
      readonly name?: string;
    };
    if (maybe.name === 'AuthRetryableFetchError') return 'order.errors.networkFailed';
    if (maybe.code === '42501') return 'order.errors.transitionUnauthorized';
    if (maybe.code === 'P0002') return 'order.errors.orderNotFound';
    if (maybe.message && /network|fetch|abort/i.test(maybe.message)) {
      return 'order.errors.networkFailed';
    }
  }

  return action === 'decline' ? 'order.errors.rejectFailed' : 'order.errors.unknown';
}
